/**
 * App - Main Application Router
 * 
 * Purpose: Root routing configuration with authentication and tenant context
 * Dependencies: react-router-dom@6, react (Suspense), @core/auth, @core/tenant, ./config/routes.config, ./layout/AppLayout, ./components/LoadingSpinner, ./components/ErrorBoundary, ./components/NotFoundOrAuthRedirect
 * Exports: App (default)
 * Side Effects: React rendering, route navigation
 * Safety: 🟡 RESTRICTED - Changes affect entire app routing
 * 
 * Agent Notes:
 * - Add new routes in routes.config.ts, NOT here
 * - DO NOT remove ProtectedRoute wrappers
 * - 2026-07-12: nested `/sales-hub/brand-studio` route (HubBrandStudioPage) — deck engine Brand Studio (Outlet child of /sales-hub).
 * - DO NOT remove error boundary
 * - Keep file under 150 lines
 * 
 * Recent Changes:
 * - 2026-07-16 [2.2.1604]: **`SETTINGS_INTEGRATIONS`** / splat → ``/settings?tab=integrations``.
 * - 2026-06-13 [2.2.1383]: **AuthGuard** on protected routes — subscription gate; **`/subscription-setup`** without AppLayout sidebar.
 * - 2026-06-09 [2.2.1361]: **`MobileShellDocumentSync`** at app root — `html[data-mobile-shell]` on all routes (auth, MFA, public).
 * - 2026-04-13: EMAIL `/email` redirects to `/messaging` (EmailToMessagingRedirect).
 * - 2026-05-09 [2.2.894]: **`ROUTES.SETTINGS_BILLING`** (`/settings/billing`) — Billing & plans (workspace settings); **`ROUTES.SETTINGS_AI_USAGE`** (`/settings/ai-usage`) — AI token usage; root **`CHANGELOG` [2.2.894]`**.
 * - 2026-04-09: [2.2.326] SETTINGS_EMAIL_ACCOUNTS (`/settings/email-accounts`) with AppLayout (inbox connection management).
 * - 2026-04-09: [2.2.325] MESSAGING_OAUTH_CALLBACK route (`/messaging/oauth/callback`) before MESSAGING (inbox OAuth return URL).
 * - 2026-04-18 [2.2.527]: **`ROUTES.TASKS`** removed — **`/tasks`** → **`NotFoundPage`**.
 * - 2026-04-18 [2.2.524]: **`ROUTES.TASKS`** at **`/tasks`** (no sidebar link; deep links and task board).
 * - 2026-05-13: Public **`/view/:shareToken`** (**`PublicPresentationViewPage`**) — read-only presentation share view without auth; gateway **`PUBLIC_PATHS`** include **`/api/v1/presentations/share/public`**.
 * - 2026-05-13 [2.2.959]: Sales Hub **`/sales-hub/viewer/:presentationId`** (**`HubPresentationViewerPage`**) — authenticated **`PresentationViewer`** from content detail **Open in viewer** (UUID presentation ids); Playwright redirect coverage in **`sales-hub.spec.ts`**; root **`CHANGELOG` [2.2.959]`**.
 * - 2026-04-18: Bug 14 — **`/activities`** route registered before **`/sales-hub`** (sibling under AppLayout, not nested).
 * - 2026-04-18: `/sales-hub/roles/:roleId` nested route (**`RoleHubRoutePage`**) for deep links and browser back.
 * - 2026-04-18 [2.2.526]: `/ai-templates` and `/ai-templates/*` redirect to `/sales-hub/create?tab=templates` (legacy bookmarks) — root **`CHANGELOG` [2.2.526]**.
 * - 2026-04-17: `/sales-hub` nested Sales Enablement Hub; `/ai-templates` was `/sales-hub/create` only.
 * - 2026-02-25: TEMPLATE_MARKETPLACE route (/templates/marketplace). [2.2.129] Skip-to-main-content link (keyboard a11y).
 * - 2026-02-08: Auth redirect fix – NOT_FOUND route wrapped in NotFoundOrAuthRedirect (unauthenticated → /auth); /dashboard alias route with ProtectedRoute; ProtectedRoute fallbackPath default /auth (core/auth).
 * - 2026-02-07: Removed SALES_INTELLIGENCE Route (route deleted in routes.config.ts; research at /home and SalesIntelligenceFormModal)
 * - 2026-02-01: Added COMPANY_DETAIL_ALIAS route (path: /company/:accountId) alongside /companies/:accountId (CompanyDetailEntry; legacy name slugs redirect).
 * - 2026-01-27: Added ASSISTANT route for AI Assistant home page
 * - 2026-06-04 [2.2.1272]: **`/opportunities`** and **`/opportunities/:id`** use **`LegacyOpportunitiesRedirect`** (query facet mapping to **`/crm/deals`**).
 * - 2026-04-30: CRM legacy redirect routes grouped with **`/crm`** block; legacy **`/territory`**, **`/contacts`**, **`/deals`**, **`/pipeline-generation`** (and detail paths) redirect to **`/crm/*`**; **`nav.ts`** sidebar drops Territory, Contacts, Deals, Pipe Gen (CRM sub-tabs via **`CrmTabNav`**).
 * - 2026-04-29: Prompt **1-D** — **`/crm`** nested **`CrmModule`** (**`CrmShell`** + **`Outlet`**); child lazy pages in **`App.tsx`** wrapped with **`crmLazyPage`** (**`Suspense`** + **`CrmOutletSkeleton`**) so CRM tab switches do not use the root full-screen fallback. **`ROUTES.CRM`** in **`routes.config.ts`**; sidebar CRM link in **`nav.ts`**.
 * - 2026-04-26: `/tenant-admin` nested routes + `TenantAdminLayout` (Langflow AI Assistant shell).
 */

import React, { Suspense, lazy } from 'react';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';

import { AuthProvider, ProtectedRoute } from '@core/auth';
import { TenantProvider } from '@core/tenant';
import { SuperAdminLayout, TenantAdminLayout } from '@design-system/layouts';

import { ROUTES } from './config/routes.config';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner';
import { MessagingShellSkeleton } from './pages/MessagingShellSkeleton';
import { NotFoundOrAuthRedirect } from './components/NotFoundOrAuthRedirect';
import { AuthGuard } from './components/auth/AuthGuard';
import LegacyOpportunitiesRedirect from './pages/LegacyOpportunitiesRedirect';
import { AppLayout } from './layout/AppLayout';
import { MobileShellDocumentSync } from './layout/mobileShell';

const HubHomePage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.HubHomePage }))
);
const HubLibraryPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.LibraryPage }))
);
const HubContentDetailPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.ContentDetailPage }))
);
const HubPresentationViewerPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.HubPresentationViewerPage }))
);
const HubCreatePage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.CreatePage }))
);
const HubSlideBuilderPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.SlideBuilderPage }))
);
const HubPlaybooksPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.PlaybooksPage }))
);
const HubAnalyticsPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.AnalyticsPage }))
);
const HubBrandStudioPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.BrandStudioPage }))
);
const HubRoleHubRoutePage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.RoleHubRoutePage }))
);
const ShareViewerPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.ShareViewerPage }))
);
const PublicShareSlugPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.PublicShareSlugPage }))
);
const ContentShareViewerPage = lazy(() =>
  import('./features/sales-enablement-hub').then((m) => ({ default: m.ContentShareViewerPage }))
);
const PublicPresentationViewPage = lazy(() => import('./pages/PublicPresentationViewPage'));
const PublicPageViewPage = lazy(() => import('./pages/PublicPageViewPage'));
const TenantAdminSectionPage = lazy(() => import('./pages/TenantAdminSectionPage'));

/** Legacy /deals/:opportunityId → /crm/deals/:opportunityId */
function LegacyDealsDetailToCrmRedirect() {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const id = opportunityId?.trim();
  return <Navigate to={id ? `/crm/deals/${encodeURIComponent(id)}` : '/crm/deals'} replace />;
}

/** Legacy /contacts/:contactId → /crm/contacts/:contactId */
function LegacyContactsDetailToCrmRedirect() {
  const { contactId } = useParams<{ contactId: string }>();
  const id = contactId?.trim();
  return <Navigate to={id ? `/crm/contacts/${encodeURIComponent(id)}` : '/crm/contacts'} replace />;
}

/**
 * Render a route with optional layout and protection
 */
function renderRoute(
  RouteComponent: React.ComponentType<unknown>,
  requiresAuth: boolean,
  showLayout: boolean = true,
  props?: Record<string, unknown>,
  requireSubscription: boolean = true,
) {
  const element = <RouteComponent {...props} />;

  if (!requiresAuth) {
    return element;
  }

  const protectedElement = (
    <AuthGuard requireSubscription={requireSubscription}>
      <ProtectedRoute>{element}</ProtectedRoute>
    </AuthGuard>
  );

  if (!showLayout) {
    return protectedElement;
  }

  return (
    <AuthGuard requireSubscription={requireSubscription}>
      <ProtectedRoute>
        <AppLayout>{element}</AppLayout>
      </ProtectedRoute>
    </AuthGuard>
  );
}

function renderMessagingRoute() {
  const MessagingPage = ROUTES.MESSAGING.component;
  return (
    <AuthGuard>
      <ProtectedRoute>
        <AppLayout>
          <Suspense fallback={<MessagingShellSkeleton />}>
            <MessagingPage />
          </Suspense>
        </AppLayout>
      </ProtectedRoute>
    </AuthGuard>
  );
}

/**
 * Render a super admin route with SuperAdminLayout
 */
function renderSuperAdminRoute(
  RouteComponent: React.ComponentType<unknown>,
  requiresAuth: boolean = true
) {
  const element = <RouteComponent />;

  if (!requiresAuth) {
    return element;
  }

  return (
    <ProtectedRoute>
      <SuperAdminLayout>{element}</SuperAdminLayout>
    </ProtectedRoute>
  );
}

/** Tenant admin shell (sidebar + Langflow AI Assistant). Requires `tenant_admin` role. */
function renderTenantAdminShell() {
  return (
    <ProtectedRoute requiredRoles={['tenant_admin']}>
      <TenantAdminLayout>
        <Outlet />
      </TenantAdminLayout>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      {/* Skip link for keyboard navigation / screen readers */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <AuthProvider>
        <MobileShellDocumentSync />
        <TenantProvider>
          <Suspense fallback={<LoadingSpinner fullScreen />}>
            <Routes>
              {/* Redirects */}
              <Route path="/login" element={<Navigate to="/auth" replace />} />
              <Route path="/register" element={<Navigate to="/auth?tab=register" replace />} />
              {/* Legacy AI Templates URL → Sales Hub Create, Templates tab (shareable deep link) */}
              <Route
                path="/ai-templates"
                element={<Navigate to="/sales-hub/create?tab=templates" replace />}
              />
              <Route
                path="/ai-templates/*"
                element={<Navigate to="/sales-hub/create?tab=templates" replace />}
              />
              {/* Legacy /opportunities → /crm/deals (preserves query via mapLegacyOpportunitiesSearchToCrmDeals) */}
              <Route path="/opportunities" element={<LegacyOpportunitiesRedirect />} />
              <Route path="/opportunities/:opportunityId" element={<LegacyOpportunitiesRedirect />} />
              {/* /dashboard is an alias for / (dashboard); redirect so direct hits don't hit 404 */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute fallbackPath="/auth">
                    <Navigate to="/" replace />
                  </ProtectedRoute>
                }
              />
              {/* Public routes */}
              <Route
                path={ROUTES.AUTH.path}
                element={renderRoute(
                  ROUTES.AUTH.component,
                  ROUTES.AUTH.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.UNAUTHORIZED.path}
                element={renderRoute(
                  ROUTES.UNAUTHORIZED.component,
                  ROUTES.UNAUTHORIZED.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.FORGOT_PASSWORD.path}
                element={renderRoute(
                  ROUTES.FORGOT_PASSWORD.component,
                  ROUTES.FORGOT_PASSWORD.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.RESET_PASSWORD.path}
                element={renderRoute(
                  ROUTES.RESET_PASSWORD.component,
                  ROUTES.RESET_PASSWORD.metadata.requiresAuth,
                  false
                )}
              />
              <Route path={ROUTES.ADMIN_LOGIN.path} element={<ROUTES.ADMIN_LOGIN.component />} />
              <Route path={ROUTES.ADMIN_DASHBOARD.path} element={<ROUTES.ADMIN_DASHBOARD.component />} />
              <Route path={ROUTES.ADMIN_TOOLS.path} element={<ROUTES.ADMIN_TOOLS.component />} />
              <Route path={ROUTES.ADMIN_TENANTS.path} element={<ROUTES.ADMIN_TENANTS.component />} />
              <Route path={ROUTES.ADMIN_VOUCHERS.path} element={<ROUTES.ADMIN_VOUCHERS.component />} />
              <Route path={ROUTES.ADMIN_TENANT_DETAIL.path} element={<ROUTES.ADMIN_TENANT_DETAIL.component />} />
              <Route path={ROUTES.ADMIN_AGENTS.path} element={<ROUTES.ADMIN_AGENTS.component />} />
              <Route path={ROUTES.ADMIN_AGENTS_NEW.path} element={<ROUTES.ADMIN_AGENTS_NEW.component />} />
              <Route path={ROUTES.ADMIN_AGENTS_EDIT.path} element={<ROUTES.ADMIN_AGENTS_EDIT.component />} />
              <Route
                path={ROUTES.PRICING.path}
                element={renderRoute(
                  ROUTES.PRICING.component,
                  ROUTES.PRICING.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.BOOKING_PUBLIC.path}
                element={renderRoute(
                  ROUTES.BOOKING_PUBLIC.component,
                  ROUTES.BOOKING_PUBLIC.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.PAYMENT_CONFIRM.path}
                element={renderRoute(
                  ROUTES.PAYMENT_CONFIRM.component,
                  ROUTES.PAYMENT_CONFIRM.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.ACCOUNT_SETUP.path}
                element={renderRoute(
                  ROUTES.ACCOUNT_SETUP.component,
                  ROUTES.ACCOUNT_SETUP.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.TENANT_SELECT.path}
                element={renderRoute(
                  ROUTES.TENANT_SELECT.component,
                  ROUTES.TENANT_SELECT.metadata.requiresAuth,
                  false
                )}
              />
              {/* Sales Hub — public read-only deck share (no auth; gateway must allow path) */}
              <Route path="/view/:shareToken" element={<PublicPresentationViewPage />} />
              <Route path="/page/:shareToken" element={<PublicPageViewPage />} />
              <Route path="/share/deck/:token" element={<ShareViewerPage />} />
              <Route path="/share/content/:token" element={<ContentShareViewerPage />} />
              <Route path="/share/:token" element={<PublicShareSlugPage />} />
              <Route
                path={ROUTES.MFA_SETUP.path}
                element={renderRoute(
                  ROUTES.MFA_SETUP.component,
                  ROUTES.MFA_SETUP.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.WELCOME.path}
                element={renderRoute(
                  ROUTES.WELCOME.component,
                  ROUTES.WELCOME.metadata.requiresAuth,
                  false
                )}
              />
              <Route
                path={ROUTES.SUBSCRIPTION_SETUP.path}
                element={renderRoute(
                  ROUTES.SUBSCRIPTION_SETUP.component,
                  ROUTES.SUBSCRIPTION_SETUP.metadata.requiresAuth,
                  false
                )}
              />

              {/* AI Assistant Home */}
              <Route
                path={ROUTES.ASSISTANT.path}
                element={renderRoute(
                  ROUTES.ASSISTANT.component,
                  ROUTES.ASSISTANT.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.LANGFLOW_ASSISTANT.path}
                element={renderRoute(
                  ROUTES.LANGFLOW_ASSISTANT.component,
                  ROUTES.LANGFLOW_ASSISTANT.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.ASSISTANT_ALIAS.path}
                element={renderRoute(
                  ROUTES.ASSISTANT_ALIAS.component,
                  ROUTES.ASSISTANT_ALIAS.metadata.requiresAuth
                )}
              />

              {/* Sales Cockpit */}
              <Route
                path={ROUTES.SALES_COCKPIT.path}
                element={renderRoute(
                  ROUTES.SALES_COCKPIT.component,
                  ROUTES.SALES_COCKPIT.metadata.requiresAuth
                )}
              />

              {/* Activity Center — app root under AppLayout only (Bug 14: must not nest under /sales-hub) */}
              <Route
                path={ROUTES.ACTIVITIES.path}
                element={renderRoute(
                  ROUTES.ACTIVITIES.component,
                  ROUTES.ACTIVITIES.metadata.requiresAuth
                )}
              />

              {/* RevOps world-model activity feed (Sprint 10) — `/activity` distinct from `/activities` inbox */}
              <Route
                path={ROUTES.REVOPS_ACTIVITY_FEED.path}
                element={renderRoute(
                  ROUTES.REVOPS_ACTIVITY_FEED.component,
                  ROUTES.REVOPS_ACTIVITY_FEED.metadata.requiresAuth
                )}
              />

              {/* Sales Hub (nested enablement shell) */}
              <Route
                path={ROUTES.SALES_HUB.path}
                element={renderRoute(
                  ROUTES.SALES_HUB.component,
                  ROUTES.SALES_HUB.metadata.requiresAuth
                )}
              >
                <Route index element={<HubHomePage />} />
                <Route path="library" element={<HubLibraryPage />} />
                <Route path="content/:id" element={<HubContentDetailPage />} />
                <Route path="viewer/:presentationId" element={<HubPresentationViewerPage />} />
                <Route path="create/*" element={<HubCreatePage />} />
                <Route path="slide-builder/:deckId" element={<HubSlideBuilderPage />} />
                <Route path="playbooks" element={<HubPlaybooksPage />} />
                <Route path="analytics" element={<HubAnalyticsPage />} />
                <Route path="brand-studio" element={<HubBrandStudioPage />} />
                <Route path="roles/:roleId" element={<HubRoleHubRoutePage />} />
              </Route>

              {/* CRM: legacy top-level bookmarks → /crm/* (same block as nested shell); sidebar uses CrmTabNav */}
              <Route
                path={ROUTES.TERRITORY.path}
                element={
                  <ProtectedRoute fallbackPath="/auth">
                    <Navigate to="/crm/accounts" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CONTACTS.path}
                element={
                  <ProtectedRoute fallbackPath="/auth">
                    <Navigate to="/crm/contacts" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CONTACT_DETAIL.path}
                element={
                  <ProtectedRoute fallbackPath="/auth">
                    <LegacyContactsDetailToCrmRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.DEALS.path}
                element={
                  <ProtectedRoute fallbackPath="/auth">
                    <Navigate to="/crm/deals" replace />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.DEAL_DETAIL.path}
                element={
                  <ProtectedRoute fallbackPath="/auth">
                    <LegacyDealsDetailToCrmRedirect />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.PIPELINE_GENERATION.path}
                element={
                  <ProtectedRoute fallbackPath="/auth">
                    <Navigate to="/crm/pipeline-generation" replace />
                  </ProtectedRoute>
                }
              />

              {/* CRM: nested routes and CrmShell live in CrmModule (Instruction 4); splat so inner `<Routes>` matches */}
              <Route
                path={`${ROUTES.CRM.path}/*`}
                element={renderRoute(ROUTES.CRM.component, ROUTES.CRM.metadata.requiresAuth)}
              />

              {/* Reports (C2 fix) */}
              <Route
                path={ROUTES.REPORTS.path}
                element={renderRoute(
                  ROUTES.REPORTS.component,
                  ROUTES.REPORTS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.REPORTS_TEAM.path}
                element={renderRoute(
                  ROUTES.REPORTS_TEAM.component,
                  ROUTES.REPORTS_TEAM.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.REPORTS_PIPELINE.path}
                element={renderRoute(
                  ROUTES.REPORTS_PIPELINE.component,
                  ROUTES.REPORTS_PIPELINE.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.REPORTS_ACTIVITY.path}
                element={renderRoute(
                  ROUTES.REPORTS_ACTIVITY.component,
                  ROUTES.REPORTS_ACTIVITY.metadata.requiresAuth
                )}
              />

              {/* Pipeline Analysis (C3 fix) */}
              <Route
                path={ROUTES.PIPELINE_OPTIMIZE.path}
                element={renderRoute(
                  ROUTES.PIPELINE_OPTIMIZE.component,
                  ROUTES.PIPELINE_OPTIMIZE.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.PIPELINE_COVERAGE.path}
                element={renderRoute(
                  ROUTES.PIPELINE_COVERAGE.component,
                  ROUTES.PIPELINE_COVERAGE.metadata.requiresAuth
                )}
              />

              {/* Presentations */}
              <Route
                path={ROUTES.PRESENTATIONS.path}
                element={renderRoute(
                  ROUTES.PRESENTATIONS.component,
                  ROUTES.PRESENTATIONS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.TEMPLATES.path}
                element={renderRoute(
                  ROUTES.TEMPLATES.component,
                  ROUTES.TEMPLATES.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.TEMPLATE_MARKETPLACE.path}
                element={renderRoute(
                  ROUTES.TEMPLATE_MARKETPLACE.component,
                  ROUTES.TEMPLATE_MARKETPLACE.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.PRESENTATION_EDIT.path}
                element={renderRoute(
                  ROUTES.PRESENTATION_EDIT.component,
                  ROUTES.PRESENTATION_EDIT.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.PRESENTATION_VIEW.path}
                element={renderRoute(
                  ROUTES.PRESENTATION_VIEW.component,
                  ROUTES.PRESENTATION_VIEW.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.PRESENTATION_VIEW_SHARE.path}
                element={renderRoute(
                  ROUTES.PRESENTATION_VIEW_SHARE.component,
                  ROUTES.PRESENTATION_VIEW_SHARE.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.COMPANY_DETAIL.path}
                element={renderRoute(
                  ROUTES.COMPANY_DETAIL.component,
                  ROUTES.COMPANY_DETAIL.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.COMPANY_DETAIL_ALIAS.path}
                element={renderRoute(
                  ROUTES.COMPANY_DETAIL_ALIAS.component,
                  ROUTES.COMPANY_DETAIL_ALIAS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.ACCOUNT_DETAIL.path}
                element={renderRoute(
                  ROUTES.ACCOUNT_DETAIL.component,
                  ROUTES.ACCOUNT_DETAIL.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.ACCOUNTS.path}
                element={renderRoute(
                  ROUTES.ACCOUNTS.component,
                  ROUTES.ACCOUNTS.metadata.requiresAuth
                )}
              />
              {/* /opportunities routes removed - redirects to /deals (H3 fix) */}
              <Route
                path={ROUTES.FINANCIAL.path}
                element={renderRoute(
                  ROUTES.FINANCIAL.component,
                  ROUTES.FINANCIAL.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.FORECAST.path}
                element={renderRoute(
                  ROUTES.FORECAST.component,
                  ROUTES.FORECAST.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.PRODUCTS.path}
                element={renderRoute(
                  ROUTES.PRODUCTS.component,
                  ROUTES.PRODUCTS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SALES_GUIDES.path}
                element={renderRoute(
                  ROUTES.SALES_GUIDES.component,
                  ROUTES.SALES_GUIDES.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.CALENDAR_EVENT.path}
                element={renderRoute(
                  ROUTES.CALENDAR_EVENT.component,
                  ROUTES.CALENDAR_EVENT.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.CALENDAR.path}
                element={renderRoute(
                  ROUTES.CALENDAR.component,
                  ROUTES.CALENDAR.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.CALLS.path}
                element={renderRoute(
                  ROUTES.CALLS.component,
                  ROUTES.CALLS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SHARED_CLIP.path}
                element={renderRoute(
                  ROUTES.SHARED_CLIP.component,
                  ROUTES.SHARED_CLIP.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.MESSAGING_OAUTH_CALLBACK.path}
                element={renderRoute(
                  ROUTES.MESSAGING_OAUTH_CALLBACK.component,
                  ROUTES.MESSAGING_OAUTH_CALLBACK.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.MESSAGING_INTEGRATIONS_OAUTH_CALLBACK.path}
                element={renderRoute(
                  ROUTES.MESSAGING_INTEGRATIONS_OAUTH_CALLBACK.component,
                  ROUTES.MESSAGING_INTEGRATIONS_OAUTH_CALLBACK.metadata.requiresAuth
                )}
              />
              <Route path={ROUTES.MESSAGING.path} element={renderMessagingRoute()} />
              <Route
                path={ROUTES.EMAIL.path}
                element={renderRoute(
                  ROUTES.EMAIL.component,
                  ROUTES.EMAIL.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.REVOPS.path}
                element={renderRoute(
                  ROUTES.REVOPS.component,
                  ROUTES.REVOPS.metadata.requiresAuth
                )}
              />

              {/* Sales Profile - Company setup & AI guide generation */}
              <Route
                path={ROUTES.SALES_PROFILE.path}
                element={renderRoute(
                  ROUTES.SALES_PROFILE.component,
                  ROUTES.SALES_PROFILE.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SALES_PROFILE_ALIAS.path}
                element={renderRoute(
                  ROUTES.SALES_PROFILE_ALIAS.component,
                  ROUTES.SALES_PROFILE_ALIAS.metadata.requiresAuth
                )}
              />

              {/* Protected routes with layout */}
              <Route
                path={ROUTES.DASHBOARD.path}
                element={renderRoute(
                  ROUTES.DASHBOARD.component,
                  ROUTES.DASHBOARD.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.WORKSPACES.path}
                element={renderRoute(
                  ROUTES.WORKSPACES.component,
                  ROUTES.WORKSPACES.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.WORKSPACE_DETAIL.path}
                element={renderRoute(
                  ROUTES.WORKSPACE_DETAIL.component,
                  ROUTES.WORKSPACE_DETAIL.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.WORKSPACE_SETTINGS.path}
                element={renderRoute(
                  ROUTES.WORKSPACE_SETTINGS.component,
                  ROUTES.WORKSPACE_SETTINGS.metadata.requiresAuth,
                  true,
                  { tab: 'settings' }
                )}
              />

              <Route
                path={ROUTES.PAGES.path}
                element={renderRoute(
                  ROUTES.PAGES.component,
                  ROUTES.PAGES.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.PAGES_ENTRY.path}
                element={renderRoute(
                  ROUTES.PAGES_ENTRY.component,
                  ROUTES.PAGES_ENTRY.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.BROWSER.path}
                element={renderRoute(
                  ROUTES.BROWSER.component,
                  ROUTES.BROWSER.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.SETTINGS.path}
                element={renderRoute(
                  ROUTES.SETTINGS.component,
                  ROUTES.SETTINGS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_PROFILE.path}
                element={renderRoute(
                  ROUTES.SETTINGS_PROFILE.component,
                  ROUTES.SETTINGS_PROFILE.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_WORKSPACE.path}
                element={renderRoute(
                  ROUTES.SETTINGS_WORKSPACE.component,
                  ROUTES.SETTINGS_WORKSPACE.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_INTEGRATIONS.path}
                element={renderRoute(
                  ROUTES.SETTINGS_INTEGRATIONS.component,
                  ROUTES.SETTINGS_INTEGRATIONS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_INTEGRATIONS_SPLAT.path}
                element={renderRoute(
                  ROUTES.SETTINGS_INTEGRATIONS_SPLAT.component,
                  ROUTES.SETTINGS_INTEGRATIONS_SPLAT.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_SECURITY.path}
                element={renderRoute(
                  ROUTES.SETTINGS_SECURITY.component,
                  ROUTES.SETTINGS_SECURITY.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_SESSIONS.path}
                element={renderRoute(
                  ROUTES.SETTINGS_SESSIONS.component,
                  ROUTES.SETTINGS_SESSIONS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_EMAIL_ACCOUNTS.path}
                element={renderRoute(
                  ROUTES.SETTINGS_EMAIL_ACCOUNTS.component,
                  ROUTES.SETTINGS_EMAIL_ACCOUNTS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_AI_USAGE.path}
                element={renderRoute(
                  ROUTES.SETTINGS_AI_USAGE.component,
                  ROUTES.SETTINGS_AI_USAGE.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SETTINGS_BILLING.path}
                element={renderRoute(
                  ROUTES.SETTINGS_BILLING.component,
                  ROUTES.SETTINGS_BILLING.metadata.requiresAuth
                )}
              />

              {/* Billing routes */}
              <Route
                path={ROUTES.BILLING.path}
                element={renderRoute(
                  ROUTES.BILLING.component,
                  ROUTES.BILLING.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.BILLING_UPGRADE.path}
                element={renderRoute(
                  ROUTES.BILLING_UPGRADE.component,
                  ROUTES.BILLING_UPGRADE.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.TEST_RESEARCH.path}
                element={renderRoute(
                  ROUTES.TEST_RESEARCH.component,
                  ROUTES.TEST_RESEARCH.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.CRM_BRAIN_FIXTURE.path}
                element={renderRoute(
                  ROUTES.CRM_BRAIN_FIXTURE.component,
                  ROUTES.CRM_BRAIN_FIXTURE.metadata.requiresAuth
                )}
              />

              <Route
                path={ROUTES.WORKFLOW_SETTINGS.path}
                element={renderRoute(
                  ROUTES.WORKFLOW_SETTINGS.component,
                  ROUTES.WORKFLOW_SETTINGS.metadata.requiresAuth
                )}
              />

              {/* Lab routes - workflows */}
              <Route
                path={ROUTES.WORKFLOWS.path}
                element={renderRoute(
                  ROUTES.WORKFLOWS.component,
                  ROUTES.WORKFLOWS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.WORKFLOW_NEW.path}
                element={renderRoute(
                  ROUTES.WORKFLOW_NEW.component,
                  ROUTES.WORKFLOW_NEW.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.WORKFLOW_DETAIL.path}
                element={renderRoute(
                  ROUTES.WORKFLOW_DETAIL.component,
                  ROUTES.WORKFLOW_DETAIL.metadata.requiresAuth
                )}
              />

              {/* Lab routes - research */}
              <Route
                path={ROUTES.RESEARCH.path}
                element={renderRoute(
                  ROUTES.RESEARCH.component,
                  ROUTES.RESEARCH.metadata.requiresAuth
                )}
              />

              {/* Super Admin routes */}
              <Route
                path={ROUTES.SUPER_ADMIN_DASHBOARD.path}
                element={renderSuperAdminRoute(
                  ROUTES.SUPER_ADMIN_DASHBOARD.component,
                  ROUTES.SUPER_ADMIN_DASHBOARD.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SUPER_ADMIN_TENANTS.path}
                element={renderSuperAdminRoute(
                  ROUTES.SUPER_ADMIN_TENANTS.component,
                  ROUTES.SUPER_ADMIN_TENANTS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SUPER_ADMIN_USERS.path}
                element={renderSuperAdminRoute(
                  ROUTES.SUPER_ADMIN_USERS.component,
                  ROUTES.SUPER_ADMIN_USERS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SUPER_ADMIN_SETTINGS.path}
                element={renderSuperAdminRoute(
                  ROUTES.SUPER_ADMIN_SETTINGS.component,
                  ROUTES.SUPER_ADMIN_SETTINGS.metadata.requiresAuth
                )}
              />
              <Route
                path={ROUTES.SUPER_ADMIN_FEATURES.path}
                element={renderSuperAdminRoute(
                  ROUTES.SUPER_ADMIN_FEATURES.component,
                  ROUTES.SUPER_ADMIN_FEATURES.metadata.requiresAuth
                )}
              />

              {/* Tenant admin (design-system shell; Langflow AI Assistant in sidebar) */}
              <Route path="/tenant-admin" element={renderTenantAdminShell()}>
                <Route index element={<TenantAdminSectionPage />} />
                <Route path="team" element={<TenantAdminSectionPage />} />
                <Route path="settings" element={<TenantAdminSectionPage />} />
                <Route path="billing" element={<TenantAdminSectionPage />} />
                <Route path="support" element={<TenantAdminSectionPage />} />
                <Route path="notifications" element={<TenantAdminSectionPage />} />
                <Route path="profile" element={<TenantAdminSectionPage />} />
              </Route>

              {/* 404 Fallback: unauthenticated users redirect to /auth instead of seeing 404 */}
              <Route
                path={ROUTES.NOT_FOUND.path}
                element={
                  <NotFoundOrAuthRedirect>
                    <ROUTES.NOT_FOUND.component />
                  </NotFoundOrAuthRedirect>
                }
              />
            </Routes>
          </Suspense>
        </TenantProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
